import React, { useState, useEffect } from "react";
import { sharingApi, recipesApi } from "../services/api";
import "./ShareRecipeModal.css";

export default function ShareRecipeModal({ onShared, onClose }) {
  const [step, setStep] = useState(1); // 1=pick recipe, 2=fill details
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [customDish, setCustomDish] = useState("");
  const [customIngredients, setCustomIngredients] = useState("");
  const [toUsername, setToUsername] = useState("");
  const [usernameCheck, setUsernameCheck] = useState(null);
  const [youtubeLink, setYoutubeLink] = useState("");
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  useEffect(() => {
    recipesApi.search("").then(setRecipes).catch(() => {});
  }, []);

  const handleUsernameChange = async (val) => {
    setToUsername(val);
    setUsernameCheck(null);
    if (val.trim().length >= 3) {
      setCheckingUsername(true);
      try {
        const result = await sharingApi.checkUsername(val.trim().replace("@", ""));
        setUsernameCheck(result);
      } catch {}
      finally { setCheckingUsername(false); }
    }
  };

  const handleSend = async () => {
    const dishName = selectedRecipe ? selectedRecipe.dish_name : customDish.trim();
    if (!dishName || !toUsername.trim()) return;

    const ingredients = selectedRecipe
      ? selectedRecipe.ingredients.map((i) => i.ingredient_name)
      : customIngredients.split(",").map((s) => s.trim()).filter(Boolean);

    setLoading(true);
    setError(null);
    try {
      await sharingApi.send({
        to_username: toUsername.trim().replace("@", ""),
        dish_name: dishName,
        ingredients,
        youtube_link: youtubeLink.trim() || null,
        instructions: instructions.trim() || null,
      });
      onShared();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to share recipe.");
    } finally {
      setLoading(false);
    }
  };

  const dishName = selectedRecipe ? selectedRecipe.dish_name : customDish;
  const canSend = dishName.trim() && toUsername.trim().length >= 3 &&
    usernameCheck && !usernameCheck.available; // username exists = valid target

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <h2 className="share-modal-title">📤 Share a Recipe</h2>
          <button className="share-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="share-error">{error}</div>}

        {/* Step 1 — Pick recipe */}
        <div className="share-section">
          <label className="share-label">Recipe</label>
          {recipes.length > 0 && (
            <div className="share-recipe-list">
              {recipes.map((r) => (
                <div
                  key={r.id}
                  className={`share-recipe-item ${selectedRecipe?.id === r.id ? "selected" : ""}`}
                  onClick={() => {
                    setSelectedRecipe(selectedRecipe?.id === r.id ? null : r);
                    setCustomDish("");
                  }}
                >
                  <span>📖 {r.dish_name}</span>
                  <span className="share-recipe-count">
                    {r.ingredients.length} ingredients
                  </span>
                </div>
              ))}
            </div>
          )}
          {!selectedRecipe && (
            <input
              placeholder="Or type a dish name manually…"
              value={customDish}
              onChange={(e) => setCustomDish(e.target.value)}
              className="share-input"
            />
          )}
          {!selectedRecipe && customDish && (
            <textarea
              placeholder="Ingredients (comma separated) e.g. Rice, Onion, Mustard seeds"
              value={customIngredients}
              onChange={(e) => setCustomIngredients(e.target.value)}
              className="share-textarea"
              rows={2}
            />
          )}
        </div>

        {/* To username */}
        <div className="share-section">
          <label className="share-label">Share with</label>
          <div className="share-username-wrap">
            <span className="share-at">@</span>
            <input
              placeholder="household username"
              value={toUsername.replace("@", "")}
              onChange={(e) => handleUsernameChange(e.target.value)}
              className="share-username-input"
            />
          </div>
          {checkingUsername && (
            <div className="share-username-status checking">Checking…</div>
          )}
          {usernameCheck && !checkingUsername && (
            <div className={`share-username-status ${usernameCheck.available ? "not-found" : "found"}`}>
              {usernameCheck.available
                ? `❌ @${usernameCheck.username} not found`
                : `✅ @${usernameCheck.username} found`}
            </div>
          )}
        </div>

        {/* YouTube link */}
        <div className="share-section">
          <label className="share-label">YouTube Recipe Link <span className="share-optional">(optional)</span></label>
          <input
            placeholder="https://youtube.com/watch?v=..."
            value={youtubeLink}
            onChange={(e) => setYoutubeLink(e.target.value)}
            className="share-input"
          />
        </div>

        {/* Instructions */}
        <div className="share-section">
          <label className="share-label">Instructions / Notes <span className="share-optional">(optional)</span></label>
          <textarea
            placeholder="Add any special notes or instructions…"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            className="share-textarea"
            rows={3}
          />
        </div>

        <div className="share-modal-actions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="share-send-btn"
            onClick={handleSend}
            disabled={!canSend || loading}
          >
            {loading ? "Sending…" : "📤 Share Recipe"}
          </button>
        </div>
      </div>
    </div>
  );
}
